import React, { Component } from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';
import Select, { components } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import PropTypes from 'prop-types';

const styles = {
  input: {
    minHeight: '37px',
    height: '37px',
  },
  isDisabled: {
    backgroundColor: '#efefef',
    borderColor: '#ced4da',
    cursor: 'not-allowed',
  },
  label: {
    fontFamily: 'Open Sans',
    marginTop: '0.3vh',
    fontSize: '12px',
  },
};

class AutoCompleteOrCreate extends Component {
  constructor(props) {
    super(props);
    const { formData, uiSchema } = props;
    const uiOptions = uiSchema?.['ui:options'] || {};
    this.state = {
      defaultValue: this.getDefaultValue(formData),
      options: this.getOptions(),
      value: this.getDefaultValue(formData),
    };
  }

  componentDidUpdate(prevProps) {
    const { formData } = this.props;
    const { options, value } = this.state;
    
    if (formData !== prevProps.formData) {
      if ((!value && formData) || (value && formData !== value.value)) {
        const matchingOption = options.find(option => option.value === formData);
        
        if (this.props.uiSchema?.['ui:options']?.creatable || matchingOption) {
          this.setState({
            value: {
              label: matchingOption ? matchingOption.label : formData,
              value: formData,
            },
          });
        }
      }
    }
  }

  onChange = option => {
    const { onChange } = this.props;
    const value = option ? option.value : undefined;
    this.setState({ value: option });
    onChange(value);
  };

  getOptions = () => {
    const { intl, schema, uiSchema } = this.props;
    const uiOptions = uiSchema?.['ui:options'] || {};
    const labels = uiOptions.enumNames || null;
    const isTranslatable = uiOptions.translatable || null;
    const values = uiOptions.enum || uiOptions.values || null;
    const items = [];

    if (schema.enum) {
      schema.enum.forEach((item, index) => {
        let label = schema.enumNames ? schema.enumNames[index] : item;
        if (isTranslatable) label = intl.formatMessage({ id: label });

        items.push({
          value: item,
          label,
        });
      });

      return items;
    }

    if (values) {
      values.forEach((item, index) => {
        let label = labels ? labels[index] : item;
        if (isTranslatable) label = intl.formatMessage({ id: label });
        items.push({ value: item, label });
      });

      return items;
    }

    return [];
  };

  getDefaultValue(value) {
    if (!value) return null;

    const { intl, schema, uiSchema } = this.props;
    const uiOptions = uiSchema?.['ui:options'] || {};
    const isTranslatable = uiOptions.translatable;
    const optionsValues = uiOptions.values;
    const { enum: enumValues, enumNames: enumLabels } = schema;

    const values = enumValues || optionsValues;
    const labels = enumLabels || values;
    const index = values ? values.indexOf(value) : null;

    if (values && values.includes(value)) {
      let label = labels[index];

      if (isTranslatable) {
        label = intl.formatMessage({ id: label });
      }
      return { value, label };
    }

    return null;
  }

  selectElement = (newValue, creatable) => {
    const { options, value } = this.state;
    const { disabled, name, uiSchema, intl, rawErrors } = this.props;
    const hasError = rawErrors && rawErrors.length > 0;

    // Align control styles with referencia-develop implementation
    let controlStyles = styles.input;
    if (disabled) controlStyles = { ...styles.input, ...styles.isDisabled };

    const customStyles = {
      control: (base, state) => ({
        ...base,
        ...controlStyles,
        borderColor: hasError ? '#ef4444' : state.isFocused ? '#80bdff' : '#ced4da',
        '&:hover': {
          borderColor: hasError ? '#ef4444' : state.isFocused ? '#80bdff' : '#ced4da',
        },
        boxShadow: state.isFocused && !hasError ? '0 0 0 0.2rem rgba(0,123,255,.25)' : null,
        backgroundColor: disabled ? '#efefef' : 'white',
        fontSize: '14px',
        fontWeight: 'normal',
        fontFamily: "'Open Sans', sans-serif",
      }),
      valueContainer: (base) => ({
        ...base,
        padding: '0 8px',
        height: '100%',
      }),
      
      indicatorSeparator: () => ({ display: 'none' }),
      singleValue: (base) => ({
        ...base,
        fontSize: '14px',
        fontWeight: 'normal',
        fontFamily: "'Open Sans', sans-serif",
        color: disabled ? 'rgba(0,0,0,0.38)' : '#212529',
      }),
      placeholder: (base) => ({
        ...base,
        fontSize: '14px',
        fontWeight: 'normal',
        fontFamily: "'Open Sans', sans-serif",
        color: '#b1b1b1',
      }),
      input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        '& input': {
          height: '100% !important',
        },
      }),
      option: (base, state) => ({
        ...base,
        fontSize: '14px',
        fontWeight: 'normal',
        fontFamily: "'Open Sans', sans-serif",
        backgroundColor: state.isSelected ? '#5882C1' : state.isFocused ? '#f0f4f9' : 'transparent',
        color: state.isSelected ? 'white' : '#374151',
        '&:active': { backgroundColor: '#5882C1' },
      }),
    };

    const commonProps = {
      options,
      formatCreateLabel: label => label,
      id: name,
      value,
      onChange: this.onChange,
      isDisabled: disabled,
      styles: customStyles,
      components: {
        Input: inputProps => <components.Input {...inputProps} data-cy={`${String(name).toLowerCase()}`} />,
      },
      isClearable: uiSchema?.['ui:options']?.clearable,
      placeholder: null,
      noOptionsMessage: () => intl.formatMessage({ id: 'NO_OPTIONS_AVAILABLE_MESSAGE' }),
    };

    if (!creatable) {
      return <Select {...commonProps} />;
    }

    return <CreatableSelect {...commonProps} />;
  };

  render() {
    const { formData, name, required, uiSchema, rawErrors } = this.props;

    return (
      <div className="w-full">
        <label 
          className="inline-block text-[12px] font-bold text-[#212529] font-['Open_Sans'] mt-[0.3vh] mb-1 tracking-tight" 
          htmlFor={name}
        >
          <FormattedMessage id={name} />
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {this.selectElement(formData, uiSchema?.['ui:options']?.creatable || false)}
        {rawErrors && rawErrors.length > 0 && (
          <p className="order-form-field-error">
            {rawErrors[0]}
          </p>
        )}
      </div>
    );
  }
}

AutoCompleteOrCreate.propTypes = {
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
  formData: PropTypes.any,
  intl: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
};

AutoCompleteOrCreate.defaultProps = {
  disabled: false,
  formData: null,
  required: false,
};

export default injectIntl(AutoCompleteOrCreate);
